import { SERVERLESS_URL } from "../constants";

export const generateImageFromPrompt = async (prompt: string, removeBackground: boolean = true): Promise<string[]> => {
  const response = await fetch(`${SERVERLESS_URL}/api/image-generation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      prompt,
      removeBackground
    })
  });

  if (!response.ok) {
    throw new Error('Failed to generate images');
  }

  const { images } = await response.json();
  console.log('Generated images:', images);
  console.log('Background removed:', removeBackground);
  return images;
};

interface StreamingImageCallback {
  onImageReceived: (index: number, url: string, metadata?: {
    backgroundRemoved?: boolean;
    backgroundRemovalError?: string | null;
  }) => void;
  onError?: (index: number, error: string) => void;
  onComplete?: () => void;
}

export const generateImageFromPromptStreaming = async (
  prompt: string, 
  removeBackground: boolean = true,
  callbacks: StreamingImageCallback
): Promise<void> => {
  const response = await fetch(`${SERVERLESS_URL}/api/image-generation-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      prompt,
      removeBackground
    })
  });

  if (!response.ok) {
    throw new Error('Failed to start image generation stream');
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        console.log('Stream ended');
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      
      // Process complete messages
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data.trim()) {
            try {
              const event = JSON.parse(data);
              
              switch (event.type) {
                case 'connected':
                  console.log('Connected to image generation stream:', event.message);
                  break;
                  
                case 'image':
                  console.log(`Image ${event.index + 1} received`);
                  const metadata = {
                    backgroundRemoved: event.backgroundRemoved,
                    backgroundRemovalError: event.backgroundRemovalError
                  };
                  callbacks.onImageReceived(event.index, event.url, metadata);
                  if (event.backgroundRemovalError) {
                    console.warn(`Image ${event.index + 1} background removal failed:`, event.backgroundRemovalError);
                  }
                  break;
                  
                case 'error':
                  console.error(`Error for image ${event.index + 1}:`, event.message);
                  if (callbacks.onError) {
                    callbacks.onError(event.index, event.message);
                  }
                  break;
                  
                case 'complete':
                  console.log('Generation complete:', event.message);
                  if (callbacks.onComplete) {
                    callbacks.onComplete();
                  }
                  break;
                  
                case 'fatal_error':
                  console.error('Fatal error:', event.message);
                  throw new Error(event.message);
                  
                default:
                  console.log('Unknown event type:', event.type);
              }
            } catch (e) {
              console.error('Failed to parse event:', data, e);
            }
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
};
